import type {
	Choice,
	ItemHighlight,
	Passage,
	ResponsePrepTime,
	Slide,
} from "@memorang/types";

export const getSortedChoices = (choiceList: Choice[]) => {
	const correctChoices = choiceList.filter(({ isCorrect }) => isCorrect);
	return correctChoices;
};

// Below this many total first selections the sample is too small to show peer stats.
const MIN_TOTAL_FIRST_SELECTIONS = 20;

export const getNumPercenetageAnswered = (
	items: Choice[],
	currentChoiceNumFirstSelections: number,
	answered?: boolean,
) => {
	const totalFirstSelections = items.reduce(
		(a, b) => a + (b.numFirstSelections || 0),
		0,
	);
	if (!answered || totalFirstSelections < MIN_TOTAL_FIRST_SELECTIONS) {
		return undefined;
	}
	return (currentChoiceNumFirstSelections / totalFirstSelections) * 100;
};

export const replaceInsertionPlaceholder = (text: string) => {
	return text.replace(/■/g, "[#stem]");
};

export const insert = (
	id: string,
	text: string,
	selectedChoiceIds: string[] | undefined,
	index?: number,
) => {
	return selectedChoiceIds?.includes(id)
		? `{{${text}}} `
		: `[#(${id}+${index})${text}] `;
};

type ComposeSessionMdParams = {
	content?: Passage | string;
	highlights?: ItemHighlight[] | null;
	insertText?: string | null;
	paragraphNumber?: number;
	choices?: Choice[];
	shouldFormat?: boolean;
	isStem?: boolean;
	selectedChoiceIds?: string[];
};

export const composeSessionMd = (params: ComposeSessionMdParams): string => {
	const {
		content,
		highlights,
		insertText,
		paragraphNumber,
		choices,
		shouldFormat = true,
		isStem = false,
		selectedChoiceIds,
	} = params;

	if (!content) {
		return "";
	}

	if (!shouldFormat) {
		if (typeof content === "string") {
			return content;
		}

		const paragraphsArray = content.paragraphs || [];

		return paragraphsArray?.map(({ text }) => text).join("\n\n");
	}

	if (typeof content === "string") {
		if (highlights?.length) {
			return highlights?.reduce((text, highlight) => {
				if (!highlight.highlightText) {
					return text;
				}
				return text.replace(
					highlight.highlightText,
					`{{${highlight.highlightText}}}`,
				);
			}, content);
		}

		const markdown =
			isStem && insertText ? `${content}\n\n> ${insertText}` : content;

		return replaceInsertionPlaceholder(markdown);
	}

	const passageParagraphs = content.paragraphs || [];

	let markdown = passageParagraphs
		?.map(({ text, number }) => {
			if (insertText && paragraphNumber === number) {
				const sentences = text.split(". ");
				const zeroChoice = choices?.find((c) => c.position === 0);
				let choiceIndex = 1;

				const baseSentence = zeroChoice
					? insert(zeroChoice.id, insertText, selectedChoiceIds, choiceIndex++)
					: "";
				text = sentences.reduce((text, sentence, index) => {
					const choice = choices?.find((c) => c.position === index + 1);
					const formatChoice = (choice: Choice | undefined) => {
						if (!choice) {
							return "";
						}
						return insert(
							choice.id,
							insertText,
							selectedChoiceIds,
							choiceIndex++,
						);
					};

					return text.concat(
						`${sentence}${index < sentences.length - 1 ? "." : ""} ${formatChoice(choice)}`,
					);
				}, baseSentence);
			}

			const highlight = highlights?.find((h) => h.paragraphNumber === number);

			if (highlight?.highlightType === "paragraph") {
				return `> ${text}`;
			}

			if (highlight?.highlightType === "text" && highlight.highlightText) {
				return `> ${text.replace(highlight.highlightText, `{{${highlight.highlightText}}}`)}`;
			}

			return text;
		})
		.join("\n\n");

	markdown = isStem && insertText ? `${content}\n\n> ${insertText}` : markdown;

	return replaceInsertionPlaceholder(markdown ?? "");
};
export const getSlidesData = (slides?: Slide[]) => {
	return (
		slides
			?.filter((slide) => slide.media?.length && slide.media?.length > 0)
			.map((slide) => ({
				image:
					slide.media?.find(
						(m) =>
							m.type === "image" || m.type === "IMAGE" || m.type === "images",
					)?.url ?? "a",
				audio:
					slide.media?.find((m) => m.type === "audio" || m.type === "AUDIO")
						?.url ?? "",
			})) ?? []
	);
};

export const getIsSlidesHasPassage = (slides?: Slide[]) => {
	return slides?.some((slide) => slide?.passage != null);
};

export const isAutoRecordMode = (
	prepTime?: ResponsePrepTime,
	timeLimit?: number,
): boolean => prepTime != null && timeLimit != null;
